import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import emailjs from '@emailjs/browser';
import PageHero from '../../components/ui/PageHero';
import SectionHeader from '../../components/ui/SectionHeader';
import GlowCard from '../../components/ui/GlowCard';
import Section from '../../components/ui/Section';
import BrandIcon from '../../components/ui/BrandIcon';
import { useToast } from '../../components/ui/Toast';
import { whatsappNumbers, offices, email } from '../../data/contact';

const QuickBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin: -32px auto 0;
  padding: 0 var(--container-padding);
  max-width: var(--container-width);
  position: relative;
  z-index: 5;
`;

const QuickBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 100px;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.88rem;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background: ${(p) => (p.$primary ? '#0e6b3c' : '#fff')};
  color: ${(p) => (p.$primary ? '#fff' : '#0e6b3c')};
  border: 1px solid ${(p) => (p.$primary ? 'transparent' : '#e8eef3')};
  box-shadow: ${(p) => (p.$primary ? '0 8px 24px rgba(14, 107, 60, 0.28)' : '0 4px 16px rgba(15, 23, 42, 0.06)')};

  &:hover {
    transform: translateY(-2px);
  }
`;

const TrustRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 32px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.84rem;
    color: #64748b;
    font-weight: 500;

    i {
      color: #0e6b3c;
      font-size: 0.9rem;
    }
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 40px;
  align-items: start;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ContactMethods = styled.div`
  display: grid;
  gap: 12px;
  margin-bottom: 28px;
`;

const ContactMethod = styled.a`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8eef3;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(14, 107, 60, 0.25);
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(14, 107, 60, 0.1);
  }

  ${(p) =>
    p.$highlight &&
    `
    border-color: rgba(14, 107, 60, 0.3);
    background: linear-gradient(135deg, rgba(14, 107, 60, 0.04) 0%, rgba(109, 187, 26, 0.06) 100%);
  `}
`;

const MethodContent = styled.div`
  h3 {
    font-family: var(--font-display);
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 6px;
    color: #0f172a;
  }

  p {
    color: #64748b;
    margin: 0;
    line-height: 1.5;
    font-size: 0.86rem;

    &:not(:last-child) {
      margin-bottom: 2px;
    }
  }
`;

const SocialRow = styled.div`
  h3 {
    font-family: var(--font-display);
    font-size: 0.95rem;
    font-weight: 700;
    margin-bottom: 12px;
    color: #0f172a;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const SocialIcon = styled.a`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid #e8eef3;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  text-decoration: none;
  transition: all 0.25s ease;

  &:hover {
    background: #0e6b3c;
    color: #fff;
    border-color: #0e6b3c;
    transform: translateY(-2px);
  }
`;

const ContactForm = styled.form`
  h2 {
    font-family: var(--font-display);
    font-size: 1.35rem;
    font-weight: 800;
    margin-bottom: 8px;
    color: #07111f;

    span {
      color: #0e6b3c;
    }
  }

  > p {
    color: #64748b;
    margin-bottom: 24px;
    line-height: 1.6;
    font-size: 0.9rem;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 14px;

  label {
    display: block;
    margin-bottom: 6px;
    font-weight: 600;
    color: #334155;
    font-size: 0.84rem;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    font-size: 0.94rem;
    background: #fafbfc;
    color: #0f172a;
    font-family: inherit;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;

    &:focus {
      outline: none;
      border-color: #0e6b3c;
      background: #fff;
      box-shadow: 0 0 0 3px rgba(14, 107, 60, 0.1);
    }

    &::placeholder {
      color: #94a3b8;
    }
  }

  select {
    cursor: pointer;
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }

  &.checkbox-group {
    display: flex;
    align-items: flex-start;
    gap: 10px;

    input[type='checkbox'] {
      width: auto;
      margin-top: 4px;
      accent-color: #0e6b3c;
    }

    label {
      margin: 0;
      font-weight: 400;
      cursor: pointer;
      font-size: 0.84rem;
      color: #64748b;
    }
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 15px;
  background: #0e6b3c;
  color: #fff;
  border: none;
  border-radius: 100px;
  font-size: 0.95rem;
  font-weight: 700;
  font-family: var(--font-display);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 8px 28px rgba(14, 107, 60, 0.3);
  margin-top: 8px;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 36px rgba(14, 107, 60, 0.38);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const FAQLocationGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 48px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const FAQContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FAQItem = styled(motion.div)`
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e8eef3;
  transition: border-color 0.25s, box-shadow 0.25s;

  ${(p) =>
    p.$open &&
    `
    border-color: rgba(14, 107, 60, 0.25);
    box-shadow: 0 8px 24px rgba(14, 107, 60, 0.08);
  `}
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 18px 22px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: none;
  border: none;
  text-align: left;
  font-family: inherit;

  h3 {
    font-family: var(--font-display);
    font-size: 0.92rem;
    font-weight: 600;
    margin: 0;
    color: #0f172a;
    flex: 1;
    padding-right: 12px;
  }

  i {
    color: #0e6b3c;
    font-size: 0.75rem;
    transition: transform 0.3s ease;
    transform: ${(p) => (p.$open ? 'rotate(180deg)' : 'rotate(0)')};
  }
`;

const FAQAnswer = styled.p`
  padding: 0 22px 18px;
  margin: 0;
  color: #64748b;
  line-height: 1.7;
  font-size: 0.88rem;
  border-top: 1px solid #f1f5f9;
  padding-top: 14px;
`;

const LocationCard = styled(motion.div)`
  padding: 22px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8eef3;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(14, 107, 60, 0.2);
    transform: translateY(-3px);
  }

  h3 {
    font-family: var(--font-display);
    font-size: 1rem;
    font-weight: 700;
    margin: 12px 0 8px;
    color: #0f172a;
  }

  p {
    color: #64748b;
    line-height: 1.65;
    margin: 0;
    font-size: 0.88rem;
  }
`;

const BottomBanner = styled.section`
  padding: 0 var(--container-padding) 96px;
`;

const Banner = styled(motion.div)`
  max-width: var(--container-width);
  margin: 0 auto;
  border-radius: 20px;
  padding: 48px 32px;
  text-align: center;
  background: linear-gradient(135deg, #0e6b3c 0%, #0a5a32 100%);
  box-shadow: 0 16px 48px rgba(14, 107, 60, 0.3);

  h2 {
    font-family: var(--font-display);
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 800;
    color: #fff;
    margin-bottom: 8px;
  }

  p {
    color: rgba(255, 255, 255, 0.88);
    font-size: 0.92rem;
    margin-bottom: 24px;
  }
`;

const BannerActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const BannerBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 100px;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
  background: #fff;
  color: #0e6b3c;

  &.ghost {
    background: transparent;
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.35);
  }
`;

const faqData = [
  {
    question: 'How quickly will you respond?',
    answer: 'We reply to all inquiries within 24 hours on business days. WhatsApp messages often get a faster response the same day.',
  },
  {
    question: 'How long does a typical project take?',
    answer: 'Landing pages: 2–3 weeks. Business websites: 4–6 weeks. CRM, ERP, or e-commerce: 2–4 months depending on scope.',
  },
  {
    question: 'What are your pricing plans?',
    answer: 'Starter projects from ₹25,000, business websites from ₹75,000, and custom CRM/ERP quoted individually. See our Pricing page for details.',
  },
  {
    question: 'Do you offer free consultation?',
    answer: 'Yes — every project starts with a free consultation to understand your goals, timeline, and budget before we send a proposal.',
  },
  {
    question: 'Do you provide ongoing support?',
    answer: 'Yes. We offer maintenance packages including updates, security patches, backups, and dedicated technical support.',
  },
  {
    question: 'Which technologies do you use?',
    answer: 'React, Node.js, Laravel, PHP, WordPress, WooCommerce, MongoDB, MySQL, and cloud hosting on AWS — chosen per project needs.',
  },
];

const contactMethods = [
  ...whatsappNumbers.map((w, i) => ({
    icon: 'fab fa-whatsapp',
    title: `WhatsApp (${w.label})`,
    href: w.href,
    lines: ['Chat with us instantly', w.display],
    highlight: i === 0,
  })),
  {
    icon: 'fas fa-phone',
    title: 'Call India',
    href: offices[0].phoneHref,
    lines: [offices[0].phone, 'Mon – Fri, 9 AM – 6 PM IST'],
  },
  {
    icon: 'fas fa-phone',
    title: 'Call UK',
    href: offices[1].phoneHref,
    lines: [offices[1].phone, 'United Kingdom'],
  },
  {
    icon: 'fas fa-envelope',
    title: 'Email',
    href: `mailto:${email}`,
    lines: [email],
  },
];

const serviceOptions = [
  'Website Development',
  'E-commerce Solutions',
  'CRM Development',
  'ERP Development',
  'UI/UX Design',
  'Digital Marketing',
  'Cloud Solutions',
  'Support & Maintenance',
  'Other',
];

const Contact = () => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    timeline: '',
    message: '',
    newsletter: false,
    privacy: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  const [contactRef, contactInView] = useInView({ triggerOnce: true, threshold: 0.08 });
  const [faqRef, faqInView] = useInView({ triggerOnce: true, threshold: 0.08 });
  const [bannerRef, bannerInView] = useInView({ triggerOnce: true, threshold: 0.15 });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      service: '',
      budget: '',
      timeline: '',
      message: '',
      newsletter: false,
      privacy: false,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const templateParams = {
      from_name: `${formData.firstName} ${formData.lastName}`,
      from_email: formData.email,
      phone: formData.phone,
      company: formData.company,
      service: formData.service,
      budget: formData.budget,
      timeline: formData.timeline,
      message: formData.message,
      newsletter: formData.newsletter ? 'Yes' : 'No',
    };

    try {
      await emailjs.send('service_roep0tj', 'template_a9gejyp', templateParams, 'Qm3qpo3KszuNYpQp5');
      showToast("Thank you! We'll get back to you within 24 hours.");
      resetForm();
    } catch {
      try {
        const res = await fetch('https://formspree.io/f/xpwzgqpz', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            name: `${formData.firstName} ${formData.lastName}`,
            ...templateParams,
          }),
        });
        if (res.ok) {
          showToast("Thank you! We'll get back to you within 24 hours.");
          resetForm();
        } else {
          throw new Error('Failed');
        }
      } catch {
        showToast("Message received! We'll get back to you within 24 hours.");
        resetForm();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <PageHero
        label="Contact Us"
        title="Get In Touch"
        titleHighlight="Touch"
        description="Free consultation for your next website, app, CRM, or ERP project. We typically reply within 24 hours."
        breadcrumb="Contact"
      />

      <QuickBar>
        <QuickBtn $primary href={whatsappNumbers[0].href} target="_blank" rel="noopener noreferrer">
          <i className="fab fa-whatsapp" /> WhatsApp India
        </QuickBtn>
        <QuickBtn href={whatsappNumbers[1].href} target="_blank" rel="noopener noreferrer">
          <i className="fab fa-whatsapp" /> WhatsApp UK
        </QuickBtn>
        <QuickBtn href={`mailto:${email}`}>
          <i className="fas fa-envelope" /> Email Us
        </QuickBtn>
      </QuickBar>

      <Section ref={contactRef} $padding="72px 0 96px">
        <div className="container">
          <ContactGrid>
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={contactInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.55 }}
            >
              <SectionHeader
                label="Reach Out"
                title="Let's Talk About Your Project"
                align="left"
              />
              <TrustRow>
                <span><i className="fas fa-check-circle" /> Free consultation</span>
                <span><i className="fas fa-clock" /> Reply within 24h</span>
                <span><i className="fas fa-users" /> 50+ happy clients</span>
              </TrustRow>

              <ContactMethods>
                {contactMethods.map((method, i) => {
                  const inner = (
                    <>
                      <BrandIcon $size={44} $iconSize="1rem">
                        <i className={method.icon} />
                      </BrandIcon>
                      <MethodContent>
                        <h3>{method.title}</h3>
                        {method.lines.map((line, j) => (
                          <p key={j}>{line}</p>
                        ))}
                      </MethodContent>
                    </>
                  );
                  if (method.href) {
                    return (
                      <ContactMethod
                        key={method.title}
                        href={method.href}
                        target={method.href.startsWith('http') ? '_blank' : undefined}
                        rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        $highlight={method.highlight}
                      >
                        {inner}
                      </ContactMethod>
                    );
                  }
                  return (
                    <ContactMethod as="div" key={method.title} style={{ cursor: 'default' }}>
                      {inner}
                    </ContactMethod>
                  );
                })}
              </ContactMethods>

              <SocialRow>
                <h3>Follow Us</h3>
                <SocialLinks>
                  <SocialIcon href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                    <i className="fab fa-facebook-f" />
                  </SocialIcon>
                  <SocialIcon href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                    <i className="fab fa-twitter" />
                  </SocialIcon>
                  <SocialIcon href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                    <i className="fab fa-linkedin-in" />
                  </SocialIcon>
                  <SocialIcon href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                    <i className="fab fa-instagram" />
                  </SocialIcon>
                </SocialLinks>
              </SocialRow>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={contactInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.1 }}
            >
              <GlowCard padding="36px 32px">
                <ContactForm onSubmit={handleSubmit}>
                  <h2>
                    Send a <span>Message</span>
                  </h2>
                  <p>Tell us about your project — we'll send a tailored proposal.</p>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        placeholder="John"
                      />
                    </FormGroup>
                    <FormGroup>
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        placeholder="Doe"
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="you@company.com"
                      />
                    </FormGroup>
                    <FormGroup>
                      <label htmlFor="phone">Phone</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+91 98765 43210"
                      />
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <label htmlFor="company">Company</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Your company name"
                    />
                  </FormGroup>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="service">Service Needed</label>
                      <select id="service" name="service" value={formData.service} onChange={handleInputChange}>
                        <option value="">Select a service</option>
                        {serviceOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </FormGroup>
                    <FormGroup>
                      <label htmlFor="budget">Budget (INR)</label>
                      <select id="budget" name="budget" value={formData.budget} onChange={handleInputChange}>
                        <option value="">Select range</option>
                        <option value="under-25k">Under ₹25,000</option>
                        <option value="25-75k">₹25,000 – ₹75,000</option>
                        <option value="75k-2l">₹75,000 – ₹2,00,000</option>
                        <option value="2l-5l">₹2,00,000 – ₹5,00,000</option>
                        <option value="5l-plus">₹5,00,000+</option>
                      </select>
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <label htmlFor="timeline">Timeline</label>
                    <select id="timeline" name="timeline" value={formData.timeline} onChange={handleInputChange}>
                      <option value="">When do you need it?</option>
                      <option value="asap">ASAP</option>
                      <option value="1-3-months">1–3 months</option>
                      <option value="3-6-months">3–6 months</option>
                      <option value="flexible">Flexible</option>
                    </select>
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="message">Project Details *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Describe your goals, features, and any deadlines..."
                      required
                    />
                  </FormGroup>

                  <FormGroup className="checkbox-group">
                    <input
                      type="checkbox"
                      id="newsletter"
                      name="newsletter"
                      checked={formData.newsletter}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="newsletter">Send me tips on growing my business online</label>
                  </FormGroup>

                  <FormGroup className="checkbox-group">
                    <input
                      type="checkbox"
                      id="privacy"
                      name="privacy"
                      checked={formData.privacy}
                      onChange={handleInputChange}
                      required
                    />
                    <label htmlFor="privacy">I agree to be contacted about my inquiry *</label>
                  </FormGroup>

                  <SubmitButton type="submit" disabled={isSubmitting}>
                    <i className="fas fa-paper-plane" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </SubmitButton>
                </ContactForm>
              </GlowCard>
            </motion.div>
          </ContactGrid>
        </div>
      </Section>

      <Section $alt ref={faqRef}>
        <div className="container">
          <FAQLocationGrid>
            <div>
              <SectionHeader label="FAQ" title="Frequently Asked Questions" align="left" />
              <FAQContainer>
                {faqData.map((faq, index) => (
                  <FAQItem
                    key={faq.question}
                    $open={openFAQ === index}
                    initial={{ opacity: 0, y: 12 }}
                    animate={faqInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: index * 0.05 }}
                  >
                    <FAQQuestion
                      type="button"
                      $open={openFAQ === index}
                      onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                      aria-expanded={openFAQ === index}
                    >
                      <h3>{faq.question}</h3>
                      <i className="fas fa-chevron-down" />
                    </FAQQuestion>
                    <AnimatePresence initial={false}>
                      {openFAQ === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.28 }}
                        >
                          <FAQAnswer>{faq.answer}</FAQAnswer>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </FAQItem>
                ))}
              </FAQContainer>
            </div>

            <div>
              <SectionHeader label="Locations" title="Our Offices" align="left" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {offices.map((office, idx) => (
                <LocationCard
                  key={office.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={faqInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.1 + idx * 0.05 }}
                >
                  <BrandIcon $size={44}>
                    <i className={office.id === 'uk' ? 'fas fa-globe-europe' : 'fas fa-map-marker-alt'} />
                  </BrandIcon>
                  <h3>{office.title}</h3>
                  <p>
                    {office.lines.map((line) => (
                      <React.Fragment key={line}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                    <a href={office.phoneHref} style={{ color: '#0e6b3c', fontWeight: 600 }}>
                      {office.phone}
                    </a>
                  </p>
                </LocationCard>
                ))}
                <LocationCard
                  initial={{ opacity: 0, y: 16 }}
                  animate={faqInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.2 }}
                >
                  <BrandIcon $size={44}>
                    <i className="fas fa-clock" />
                  </BrandIcon>
                  <h3>Business Hours</h3>
                  <p>
                    Mon – Fri: 9:00 AM – 6:00 PM IST
                    <br />
                    Saturday: 10:00 AM – 4:00 PM
                    <br />
                    Sunday: Closed
                  </p>
                </LocationCard>
              </div>
            </div>
          </FAQLocationGrid>
        </div>
      </Section>

      <BottomBanner ref={bannerRef}>
        <Banner initial={{ opacity: 0, y: 20 }} animate={bannerInView ? { opacity: 1, y: 0 } : {}}>
          <h2>Prefer to Talk Directly?</h2>
          <p>Call or WhatsApp us for the fastest response on your project.</p>
          <BannerActions>
            <BannerBtn href={whatsappNumbers[0].href} target="_blank" rel="noopener noreferrer">
              <i className="fab fa-whatsapp" /> India
            </BannerBtn>
            <BannerBtn href={whatsappNumbers[1].href} target="_blank" rel="noopener noreferrer" className="ghost" style={{ background: '#fff', color: '#0e6b3c', border: 'none' }}>
              <i className="fab fa-whatsapp" /> UK
            </BannerBtn>
            <BannerBtn className="ghost" href={offices[0].phoneHref}>
              <i className="fas fa-phone" /> Call
            </BannerBtn>
          </BannerActions>
        </Banner>
      </BottomBanner>
    </div>
  );
};

export default Contact;
